import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Linkedin, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        if (import.meta.env.DEV) {
          console.log("AuthCallback: Processing authentication callback...");
          console.log("URL:", window.location.href);
          console.log("Hash:", window.location.hash);
          console.log("Search:", window.location.search);
        }

        // Check URL hash for error parameters from Supabase
        const hash = window.location.hash;
        if (hash.includes('error=')) {
          const params = new URLSearchParams(hash.substring(1));
          const errorCode = params.get('error_code');
          const errorDesc = params.get('error_description');
          if (import.meta.env.DEV) console.error('Auth error from URL hash:', errorCode, errorDesc);
          setErrorMessage(errorDesc?.replace(/\+/g, ' ') || "An error occurred during authentication");
          setStatus("error");
          return;
        }

        // Check URL query params for error (some flows use query params)
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('error')) {
          const errorDesc = urlParams.get('error_description');
          if (import.meta.env.DEV) console.error('Auth error from URL query:', urlParams.get('error'), errorDesc);
          setErrorMessage(errorDesc?.replace(/\+/g, ' ') || "An error occurred during authentication");
          setStatus("error");
          return;
        }

        // Check URL query params for token_hash (PKCE flow)
        const tokenHash = urlParams.get('token_hash');
        const type = urlParams.get('type');

        if (tokenHash && type) {
          if (import.meta.env.DEV) console.log('Verifying token from URL, type:', type);
          
          // Verify the OTP token
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type as any, // signup, recovery, invite, magiclink, etc.
          });

          if (error) {
            if (import.meta.env.DEV) console.error('Token verification failed:', error);
            setErrorMessage(error.message || "Failed to verify email. The link may have expired.");
            setStatus("error");
            return;
          }

          if (data.session) {
            if (import.meta.env.DEV) console.log('Token verified successfully, session established');
            
            // Mark email as verified in metadata so Onboarding skips Step 1
            await supabase.auth.updateUser({
              data: { msg91_email_verified: true }
            });

            setStatus("success");
            toast.success("Email verified successfully!");
            
            // Clean up URL and redirect to home after short delay
            window.history.replaceState({}, '', '/auth/callback');
            setTimeout(() => {
              navigate("/");
            }, 1500);
            return;
          }
        }

        // Check for access_token in hash (implicit grant flow)
        if (hash.includes('access_token=')) {
          if (import.meta.env.DEV) console.log('Found access_token in hash, processing...');
          
          // Poll for session with exponential backoff instead of fixed delay
          const maxAttempts = 5;
          let attempts = 0;
          let delay = 100;
          
          const pollForSession = async (): Promise<boolean> => {
            const { data: { session }, error } = await supabase.auth.getSession();
            
            if (error) {
              if (import.meta.env.DEV) console.error('Session error:', error);
              setErrorMessage(error.message);
              setStatus("error");
              return false;
            }

            if (session) {
              if (import.meta.env.DEV) console.log('Session established from hash');
              
              // Mark email as verified in metadata so Onboarding skips Step 1
              await supabase.auth.updateUser({
                data: { msg91_email_verified: true }
              });

              setStatus("success");
              toast.success("Email verified successfully!");
              
              // Clean up URL and redirect
              window.history.replaceState({}, '', '/auth/callback');
              setTimeout(() => {
                navigate("/");
              }, 1500);
              return true;
            }
            
            return false;
          };
          
          // Try immediately, then with exponential backoff
          while (attempts < maxAttempts) {
            const success = await pollForSession();
            if (success) return;
            
            attempts++;
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, delay));
              delay = Math.min(delay * 2, 2000); // Max 2 second delay
            }
          }
          
          // If we get here, session wasn't established after all attempts
          setErrorMessage("Session could not be established. Please try again.");
          setStatus("error");
          return;
        }

        // No token in URL - check if already authenticated (user may have navigated here directly)
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          if (import.meta.env.DEV) console.log('Already authenticated, redirecting to home...');
          setStatus("success");
          toast.success("You're already signed in!");
          setTimeout(() => navigate("/"), 1000);
          return;
        }

        // No valid token or session found
        if (import.meta.env.DEV) console.log('No valid token found in URL');
        setErrorMessage("No verification token found. Please try clicking the link in your email again, or request a new verification email.");
        setStatus("error");

      } catch (err: any) {
        if (import.meta.env.DEV) console.error('Auth callback error:', err);
        setErrorMessage(err.message || "An unexpected error occurred");
        setStatus("error");
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-[400px] space-y-8 text-center animate-in fade-in duration-500">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary text-primary-foreground">
            <Linkedin className="w-5 h-5" />
          </div>
          <span className="text-xl font-bold">WDC Today</span>
        </div>

        {status === "loading" && (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin" />
            <h2 className="text-2xl font-bold">Verifying your email...</h2>
            <p className="text-muted-foreground">
              Please wait while we confirm your account.
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-4">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500" />
            <h2 className="text-2xl font-bold">Email Verified!</h2>
            <p className="text-muted-foreground">
              Your email has been verified. Redirecting you to the dashboard...
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4">
            <AlertCircle className="w-16 h-16 mx-auto text-destructive" />
            <h2 className="text-2xl font-bold">Verification Failed</h2>
            <p className="text-muted-foreground">
              {errorMessage}
            </p>
            <div className="flex flex-col gap-2 pt-4">
              <Button onClick={() => navigate("/auth")} className="w-full">
                Go to Sign In
              </Button>
              <p className="text-sm text-muted-foreground mt-2">
                Need a new verification email? Sign in with your email and password to request one.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
